/** The literal `forbidAccess` prefixes every authorization failure with, server-side. */
const AUTHORIZATION_DENIED = 'Privileges not sufficient'

/** The shape of an Apollo error this needs, kept narrow so tests need not build a real one. */
type MaybeApolloError = {graphQLErrors?: ReadonlyArray<{message?: string} | null> | null} | null

/**
 * Whether a GraphQL failure is the server refusing access, as opposed to the request not arriving.
 *
 * The distinction decides what the page can say. A refusal is a settled answer worth explaining to
 * the viewer; a transport failure is neither, and presenting one as the other would tell a Money
 * Manager their own network is off-limits every time the connection drops.
 *
 * Matching the message is a coupling to the server's `forbidAccess` wording, accepted because
 * GraphQL carries no machine-readable code on these errors and the alternative — treating every
 * failure as a refusal — is wrong in the more damaging direction.
 *
 * @param error - The Apollo error, if the query produced one.
 * @returns `true` when at least one GraphQL error is an authorization denial.
 */
export const isAuthorizationDenied = (error?: MaybeApolloError): boolean =>
  (error?.graphQLErrors ?? []).some((graphQLError) => graphQLError?.message?.includes(AUTHORIZATION_DENIED) ?? false)
